import {
  Formik,
  Form,
  FormikProvider,
  FieldArray,
  ErrorMessage,
  FormikProps,
} from "formik";
import React, { useEffect, useState } from "react";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
} from "../../core-component/ui";
import {
  ICFormikDropDown,
  ICFormikSwitch,
  ICFormikTextInput,
} from "../../core-component/formik";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  handleError,
  hasAnyModification,
  sweetAlertError,
} from "../../utils/AppFunctions";
import Loader from "../../core-component/ui/Loader";
import {
  getGenderDdl,
  IMeasurementType,
} from "./MeasurementTypeListController";
import {
  fetchMeasurementTypeById,
  getMeasurementFieldDdl,
  handleSubmitMeasurementType,
  IConfiguration,
  IMeasurementFieldItem,
  IMeasurementTypeForm,
  MEASUREMENT_TYPE_INITIAL_VALUES,
  measurementTypeValidationSchema,
} from "./MeasurementTypeFormController";
import { IOption } from "../../data/AppInterface";
import { BIT_VALUE, IMAGE_TYPE } from "../../utils/AppEnumerations";
import {
  ICDropDown,
  ICFileUpload,
  ICTextInput,
} from "../../core-component/form";
import { deleteIcon } from "../../assets/icons/SvgIconList";
import { cn } from "../../lib";
import { fetchUploadImageService } from "../global/image-service/ImageServices";
import { MESSAGE_UNKNOWN_ERROR_OCCURRED } from "../../utils/AppConstants";
interface IMeasurementTypeFormProps {
  handleClose: (status: boolean) => void;
  readOnly: boolean;
  measurementTypeToEdit?: IMeasurementType;
}

const MeasurementFieldDropdown = ({
  options,
  selected,
  onSelect,
  errorMessage,
  disabled,
}: {
  options: IOption<string, IMeasurementFieldItem>[];
  selected: string | undefined;
  onSelect: (option: IOption<string, IMeasurementFieldItem>) => void;
  errorMessage: JSX.Element | undefined;
  disabled: boolean;
}) => {
  const { t } = UseTranslationHook();
  return (
    <ICDropDown<string, IMeasurementFieldItem>
      options={options}
      label={t("field")}
      selected={selected ?? undefined}
      onSelect={onSelect}
      disabled={disabled}
      errorMessage={errorMessage}
    />
  );
};
const MeasurementUnitInput = ({
  name,
  value,
  disabled,
}: {
  name: string;
  value: string;
  disabled: boolean;
}) => {
  const { t } = UseTranslationHook();

  return (
    <ICTextInput
      label={t("unit")}
      name={name}
      value={value}
      containerClassName="ml-4"
      disabled={disabled}
    />
  );
};

const SortOrderInput = ({
  name,
  value,
  onChange,
  errorMessage,
  disabled,
}: {
  name: string;
  value: number | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errorMessage: JSX.Element | undefined;
  disabled: boolean;
}) => {
  const { t } = UseTranslationHook();
  return (
    <ICTextInput
      type="number"
      label={t("order")}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      errorMessage={errorMessage}
    />
  );
};

const MeasurementItem = ({
  index,
  item,
  arrayHelpers,
  formikProps,
  readOnly,
  touched,
  values,
  measurementField,
}: {
  index: number;
  item: IConfiguration;
  arrayHelpers: any;
  formikProps: any;
  readOnly: boolean;
  touched: any;
  values: IMeasurementTypeForm;
  measurementField: IOption<string, IMeasurementFieldItem>[];
}) => {
  const errorMessageMeasurementField =
    values?.configuration?.[index] &&
    !values.configuration[index].measurement_field_id &&
    touched?.configuration?.[index]?.measurement_field_id ? (
      <ErrorMessage name={`configuration[${index}].measurement_field_id`} />
    ) : undefined;
  const errorMessageSortOrder =
    values?.configuration?.[index] &&
    !values.configuration[index].sort_order &&
    touched?.configuration?.[index]?.sort_order ? (
      <ErrorMessage name={`configuration[${index}].sort_order`} />
    ) : undefined;

  const handleSortOrderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formikProps.handleChange(e);
    arrayHelpers.replace(index, {
      ...item,
      sort_order: e.target.value,
    });
  };
  const filterOptions = (
    index: number,
    formikPorps: FormikProps<IMeasurementTypeForm>,
  ) => {
    const currentValue =
      formikPorps.values.configuration[index]?.measurement_field_id;

    const selectedValues = formikPorps.values.configuration
      .map((item, i) => (i !== index ? item.measurement_field_id : null))
      .filter((val) => val !== null);

    return measurementField.filter(
      (option) =>
        option.data_value === currentValue ||
        !selectedValues.includes(option.data_value),
    );
  };

  return (
    <div className="flex w-full items-center mt-4">
      <div className="grid grid-cols-3">
        <div className="col-span-2">
          <MeasurementFieldDropdown
            options={filterOptions(index, formikProps)}
            selected={item.measurement_field_id ?? undefined}
            onSelect={(option) => {
              arrayHelpers.replace(index, {
                ...item,
                measurement_field_id: option.data_value,
                measurement_unit: option.items?.measurement_unit,
                measurement_unit_label: option.items?.measurement_unit_label,
              });
            }}
            errorMessage={errorMessageMeasurementField}
            disabled={readOnly}
          />
        </div>

        <MeasurementUnitInput
          name={`configuration[${index}].measurement_unit`}
          value={item.measurement_unit_label}
          disabled
        />
      </div>

      <div className="w-2/6 ml-4">
        <SortOrderInput
          name={`configuration[${index}].sort_order`}
          value={item.sort_order ?? undefined}
          onChange={handleSortOrderChange}
          errorMessage={errorMessageSortOrder}
          disabled={readOnly}
        />
      </div>

      {!readOnly && (
        <div
          onClick={() =>
            values.configuration.length > 1 && arrayHelpers.remove(index)
          }
          className={cn("ml-2 h-10 flex items-center", {
            "cursor-pointer": values.configuration.length > 1,
          })}
        >
          <div className="size-5">
            {values.configuration.length > 1 && deleteIcon}
          </div>
        </div>
      )}
    </div>
  );
};

const MeasurementTypeForm: React.FC<IMeasurementTypeFormProps> = ({
  handleClose,
  measurementTypeToEdit,
  readOnly,
}) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [typeImage, setTypeImage] = useState<string[]>([]);
  const [gender, setGender] = useState<IOption<string>[]>([]);
  const [measurementField, setMeasurementField] = useState<
    IOption<string, IMeasurementFieldItem>[]
  >([]);

  const [
    measurementTypeFormInitialValues,
    setMeasurementTypeFormInitialValues,
  ] = useState<IMeasurementTypeForm>();

  useEffect(() => {
    if (measurementTypeToEdit) {
      getMeasurementTypeById(measurementTypeToEdit.id);
    } else {
      setMeasurementTypeFormInitialValues(MEASUREMENT_TYPE_INITIAL_VALUES);
      fetchGender();
      getMeasurementFieldDdl(setMeasurementField, []);
    }
  }, []);

  useEffect(() => {
    setTypeImage(
      measurementTypeFormInitialValues?.image_url
        ? [measurementTypeFormInitialValues.image_url]
        : [],
    );
  }, [measurementTypeFormInitialValues]);

  const getMeasurementTypeById = async (id: string) => {
    setLoading(true);

    const measurementType = await fetchMeasurementTypeById(id, handleClose);
    setMeasurementTypeFormInitialValues(measurementType);
    fetchGender(measurementType.gender);

    const measurementFieldIds =
      measurementType?.configuration &&
      measurementType.configuration.length > 0 &&
      measurementType.configuration.map(
        (config: IConfiguration) => config.measurement_field_id,
      );

    await getMeasurementFieldDdl(setMeasurementField, measurementFieldIds);
    setLoading(false);
  };

  const fetchGender = async (genderValue?: string) => {
    setLoading(true);
    await getGenderDdl(setGender, genderValue);
    setLoading(false);
  };

  const checkFormModification = (values: IMeasurementTypeForm) => {
    if (
      measurementTypeFormInitialValues &&
      hasAnyModification(values, measurementTypeFormInitialValues)
    ) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  const handleSubmit = async (values: IMeasurementTypeForm) => {
    try {
      setSubmitLoading(true);
      const updatedTypeMedia = typeImage
        ? typeImage.map((url) => ({
            media_url: url,
          }))
        : [];

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append(`ImageFile`, file);
        formData.append(`type`, IMAGE_TYPE.MeasurementType);
        values.id && formData.append(`typeId`, values.id.toString());

        const mediaUrl = await fetchUploadImageService(formData);
        if (!mediaUrl) {
          return null;
        }

        const newMediaItem = {
          media_url: mediaUrl,
        };
        updatedTypeMedia.push(newMediaItem);
      }

      const filteredConfigurations = values.configuration.reduce<
        IConfiguration[]
      >((acc, item) => {
        if (
          item.measurement_field_id !== null &&
          item.measurement_unit.trim() !== "" &&
          item.sort_order !== undefined
        ) {
          acc.push({ ...item, sort_order: Number(item.sort_order) });
        }
        return acc;
      }, []);

      await handleSubmitMeasurementType(
        {
          ...values,
          configuration: filteredConfigurations,
          image_url: updatedTypeMedia[0]?.media_url || "",
          gender: values.gender,
        },
        setSubmitLoading,
        measurementTypeToEdit?.id,
        handleClose,
      );
    } catch (e: any) {
      sweetAlertError({ message: MESSAGE_UNKNOWN_ERROR_OCCURRED });
      handleError(e, "measurementTypeSubmit");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {measurementTypeFormInitialValues && (
            <Formik
              initialValues={measurementTypeFormInitialValues}
              validationSchema={measurementTypeValidationSchema(t)}
              onSubmit={(values) => {
                handleSubmit(values);
              }}
            >
              {(formikPorps) => {
                const { handleSubmit, values, touched } = formikPorps;

                return (
                  <FormRightModalWrapper
                    containerClassName="!w-1/3"
                    onClose={() => checkFormModification(values)}
                    title={t("measurement_type")}
                    footer={{
                      created_date:
                        measurementTypeFormInitialValues.created_date,
                      created_by: measurementTypeFormInitialValues.created_by,
                      modified_by: measurementTypeFormInitialValues.modified_by,
                      modified_date:
                        measurementTypeFormInitialValues.modified_date,
                    }}
                  >
                    <Form
                      onSubmit={handleSubmit}
                      className="flex flex-col h-full"
                    >
                      <div className="flex-1 overflow-y-scroll pt-2.5 mb-4 space-y-4">
                        <ICFormikTextInput
                          {...formikPorps}
                          fieldName={"type_name"}
                          placeholder={t("type_name")}
                          required
                          tabIndex={0}
                          disabled={readOnly}
                        />
                        <div className="grid grid-cols-2 mt-4">
                          <ICFormikTextInput
                            {...formikPorps}
                            fieldName={"turn_around_time"}
                            placeholder={t("turn_around_time")}
                            type="number"
                            required
                            tabIndex={0}
                            disabled={readOnly}
                          />
                          <ICFormikTextInput
                            {...formikPorps}
                            fieldName={"job_completion_time"}
                            placeholder={t("job_completion_time")}
                            type="number"
                            containerClassName="ml-4"
                            required
                            tabIndex={0}
                            disabled={readOnly}
                          />
                        </div>
                        <div className="grid grid-cols-2 ">
                          <ICFormikDropDown
                            {...formikPorps}
                            containerFormikClassName="mt-4"
                            name={"gender"}
                            placeholder={t("gender")}
                            options={gender}
                            required
                            disabled={readOnly}
                          />
                          <ICFormikSwitch
                            {...formikPorps}
                            name={"is_active"}
                            containerClassName="mt-4 ml-4 gap-4 !w-fit"
                            placeholder={t("status")}
                            checkedValue={BIT_VALUE.One}
                            unCheckedValue={BIT_VALUE.Zero}
                            disabled={readOnly}
                          />
                        </div>
                        <FormikProvider value={formikPorps}>
                          <div className="flex gap-4">
                            <FieldArray
                              name="configuration"
                              render={(arrayHelpers) => (
                                <div className="w-full">
                                  {formikPorps?.values?.configuration?.map(
                                    (item: IConfiguration, index: number) => (
                                      <MeasurementItem
                                        key={item.measurement_field_id || index}
                                        index={index}
                                        item={item}
                                        arrayHelpers={arrayHelpers}
                                        formikProps={formikPorps}
                                        readOnly={readOnly}
                                        touched={touched}
                                        values={values}
                                        measurementField={measurementField}
                                      />
                                    ),
                                  )}
                                  {!readOnly && (
                                    <div className="mt-4">
                                      <ICButton
                                        type="button"
                                        onClick={() => {
                                          arrayHelpers.push({
                                            measurement_field_id: null,
                                            measurement_unit: "",
                                            sort_order: null,
                                          });
                                        }}
                                      >
                                        {t("add_configuration")}
                                      </ICButton>
                                    </div>
                                  )}
                                </div>
                              )}
                            />
                          </div>
                        </FormikProvider>

                        <div className="mt-4">
                          <ICFileUpload
                            title={t("upload_image")}
                            maxFiles={1}
                            maxFileSizeMB={10}
                            acceptedFileTypes={["jpg", "png", "jpeg"]}
                            onFilesSelected={setSelectedFiles}
                            selectedFiles={selectedFiles}
                            setSelectedFiles={setSelectedFiles}
                            data={typeImage}
                            setData={setTypeImage}
                            disabled={readOnly}
                          />
                        </div>
                      </div>
                      {!readOnly && (
                        <div className="py-4">
                          <ICButton
                            loading={submitLoading}
                            className={"w-full"}
                            children={t("submit")}
                          />
                        </div>
                      )}
                    </Form>
                  </FormRightModalWrapper>
                );
              }}
            </Formik>
          )}
          {showDiscardWarning && (
            <ICConfirmationModal
              title={t("discard_warning")}
              message={t("discard_unsaved_changes")}
              onClose={(value) => {
                setShowDiscardWarning(false);
                value && handleClose(false);
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default MeasurementTypeForm;
